import { useEffect, useState } from 'react'
import { useSettings } from '@/settings/hooks/useSettings'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import DefaultJurisdictionSettingsDialog from '@/settings/components/DefaultJurisdictionSettingsDialog'
import { getJurisdictionLabel } from '@/jurisdiction-mappings/jurisdiction-maps'
import { getDefaultJurisdictionAnalyticsProps } from '@/analytics/utils/matter-jurisdiction-analytics'
import { getCustomInstructionsAnalyticsProps } from '@/analytics/utils/custom-instructions-analytics'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import CustomInstructionsCard from '@/settings/components/CustomInstructionsCard'
import SectionHeader from '@/settings/components/SectionHeader'
import { SettingsPanelShell } from '../components/SettingsPanelShell'

/**
 * Preferences section: the Matter Settings (Default Jurisdiction) and Personalization (Custom
 * Instructions) cards ported from the legacy settings page, each with their existing Configure
 * dialogs. Connected accounts is now its own section, so it is not rendered here.
 */
export function PreferencesPanel() {
  const { getSettings, updateUserSettings, defaultJurisdiction, customInstructions, settingsLoaded } = useSettings()
  const [jurisdictionDialogOpen, setJurisdictionDialogOpen] = useState(false)
  const { trackEvent } = useAnalytics()

  useEffect(() => {
    getSettings()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- Keep the existing mount-only settings fetch behavior.

  const handleSaveCustomInstructions = async (nextInstructions: string) => {
    await updateUserSettings({ custom_instructions: nextInstructions })
    trackEvent(AnalyticsEvent.CustomInstructionsSaved, getCustomInstructionsAnalyticsProps(nextInstructions))
  }

  const handleSaveDefaultJurisdiction = async (jurisdiction: string | null) => {
    await updateUserSettings({ default_jurisdiction: jurisdiction })
    trackEvent(AnalyticsEvent.DefaultJurisdictionSaved, getDefaultJurisdictionAnalyticsProps(jurisdiction))
  }

  const handleConfigureDefaultJurisdiction = () => {
    trackEvent(AnalyticsEvent.DefaultJurisdictionConfigureClicked, { hasExistingDefaultJurisdiction: Boolean(defaultJurisdiction) })
    setJurisdictionDialogOpen(true)
  }

  return (
    <SettingsPanelShell title="Preferences">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col overflow-hidden rounded-lg border border-brand-neutral-200 bg-white">
          <SectionHeader title="Matter Settings" />
          <div className="py-4">
            <div className="flex flex-col gap-3 px-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="font-semibold leading-7 text-brand-neutral-950">Default Jurisdiction</p>
                {!settingsLoaded ? (
                  <div className="flex min-h-5 items-center" role="status" aria-label="Loading default jurisdiction">
                    <CircularProgressContinuousSized size={14} thickness={6} />
                  </div>
                ) : defaultJurisdiction ? (
                  <div className="flex flex-wrap items-center gap-x-2.5 text-sm leading-5 tracking-[-0.15px]">
                    <span className="font-semibold text-brand-neutral-800">{getJurisdictionLabel(defaultJurisdiction)}</span>
                    <span className="hidden h-3.5 border-l border-brand-neutral-400 sm:inline-block" aria-hidden="true" />
                    <span className="text-brand-neutral-600">You can change this when creating each matter.</span>
                  </div>
                ) : (
                  <p className="text-sm leading-5 tracking-[-0.15px] text-brand-neutral-600">
                    Select a jurisdiction to auto-fill new matters. You can override anytime during matter creation.
                  </p>
                )}
              </div>
              <Button
                variant="tonal"
                color={defaultJurisdiction ? 'neutral' : 'primary'}
                size="sm"
                onClick={handleConfigureDefaultJurisdiction}
                disabled={!settingsLoaded}
                className="self-start sm:self-auto"
              >
                {defaultJurisdiction ? 'Edit' : 'Configure'}
              </Button>
            </div>
          </div>
        </div>

        <CustomInstructionsCard savedInstructions={customInstructions} settingsLoaded={settingsLoaded} onSave={handleSaveCustomInstructions} />
      </div>

      <DefaultJurisdictionSettingsDialog
        open={jurisdictionDialogOpen}
        onOpenChange={setJurisdictionDialogOpen}
        initialJurisdiction={defaultJurisdiction}
        onSave={handleSaveDefaultJurisdiction}
      />
    </SettingsPanelShell>
  )
}
